import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchSelectOptions } from '@/components/formBuilder/helpers/formHelpers';

export interface SurveyTemplate {
  id?: string | number;
  name?: string;
  title?: string;
  subject?: string;
  body?: string;
  [key: string]: unknown;
}

const CATEGORY = 'survey_notice';

/** `filter_by[name]=` is left open so typed text appends for a server search. */
export const buildTemplateEndpoint = (name = '') =>
  `/ats_comm_templates?filter_by[channel]=email&filter_by[category][]=${CATEGORY}&items=200&filter_by[name]=${name}`;

/** Default template names to preselect, tried in order. */
export const initialTemplateNames = (surveyType: string): string[] =>
  surveyType === 'candidate'
    ? [
        'Candidate Survey Template',
        'Candidate survey email',
        'Candidate survey email template',
        'Candidate survey ',
        'Candidate survey template',
      ]
    : [
        'HM Survey Template',
        'HM survey email',
        'HM survey email template',
        'HM survey ',
        'Hiring Manager Survey Template',
      ];

const matches = (template: SurveyTemplate, name: string) =>
  template?.title?.toLowerCase() === name?.toLowerCase() ||
  template?.name?.toLowerCase() === name?.toLowerCase();

export const findMatchingTemplate = (
  templates: SurveyTemplate[],
  names: string[]
): SurveyTemplate | null => {
  for (const name of names) {
    const match = templates.find((template) => matches(template, name));
    if (match) return match;
  }
  return null;
};

interface UseSurveyTemplatesArgs {
  surveyType: string;
  // Edit mode: an already-chosen template, so no name-based preselect.
  selectedTemplate?: SurveyTemplate | null;
  isEditMode?: boolean;
  onSelect?: (_template: SurveyTemplate) => void;
}

/**
 * Owns the survey template list so the picker can live inside the email card.
 *
 * Replaces `ChooseSurveyTemplate`, which rendered its own select above the
 * card — two pickers for one field once kula's card gained its own.
 */
export const useSurveyTemplates = ({
  surveyType,
  selectedTemplate,
  isEditMode = false,
  onSelect,
}: UseSurveyTemplatesArgs) => {
  const [templates, setTemplates] = useState<SurveyTemplate[]>([]);
  const applied = useRef(false);

  // Latest handler without re-running the fetch on every parent render.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const hasSelection = !!selectedTemplate?.id;

  useEffect(() => {
    let live = true;

    (async () => {
      const list = (await fetchSelectOptions({
        endPoint: buildTemplateEndpoint(),
      })) as SurveyTemplate[];
      if (!live) return;

      setTemplates(list ?? []);

      // Preselect by name on create only — an edit already has its template.
      if (!list?.length || applied.current || hasSelection || isEditMode)
        return;
      const match = findMatchingTemplate(
        list,
        initialTemplateNames(surveyType)
      );
      if (match) {
        applied.current = true;
        onSelectRef.current?.(match);
      }
    })();

    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyType, isEditMode]);

  const loadTemplates = useCallback(async (input: string) => {
    const list = (await fetchSelectOptions({
      endPoint: buildTemplateEndpoint(input ?? ''),
    })) as SurveyTemplate[];
    return list ?? [];
  }, []);

  return { templates, loadTemplates };
};
